import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fundingAuthAPI,
  getFundingToken,
  isAccountDeactivatedError,
  isAccountSuspendedError,
  markIpeAuthReady,
  resetIpeAuthReady,
  setFundingToken,
  setIpeAccountDeactivatedHandler,
  setIpeAuthRequiredHandler,
  setIpeCreditsInsufficientHandler,
  setToken,
} from '@/utils/api';
import {
  IPE_LOGIN_PATH,
  buildLoginUrlWithRedirect,
  isIpeLoginPath,
  isSafeWorkbenchRedirect,
} from '@/config/ipeRoutes';
import IpeCreditsInsufficientDialog from '@/components/ipe/IpeCreditsInsufficientDialog';
import { showToast } from '@/utils/toast';
import { validateIpePassword } from '@/utils/ipePasswordValidation';
import { msUntilJwtCalendarExpiry } from '@/utils/jwtDailyExpiry';
import { supplementLoginLocationAfterAuth } from '@/utils/deviceLocation';
import { setImageSpaceCreatorContext, syncImageSpaceCreatorContext } from '@/utils/imageSpaceCreatorMeta';
import {
  clearIpeSessionCache,
  readIpeSessionCache,
  saveIpeSessionCache,
} from '@/utils/ipeSessionCache';
import {
  bindIpeUserLocalOwner,
  clearIpeUserLocalState,
} from '@/utils/ipeUserLocalState';
import '@/utils/ipeUserLocalRegistrations';
import '@/styles/ipe/ipe-auth.css';

const IpeAuthContext = createContext(null);

const phonePattern = /^1[3-9]\d{9}$/;

function resolveIpeOwnerId(user) {
  if (!user) return null;
  const id = user.id ?? user._id;
  const normalized = id == null ? '' : String(id).trim();
  return normalized || null;
}

/**
 * 在 setUser / setSessionVerified 之前同步 bind。
 * 避免子组件首屏 useState(() => loadCache()) 早于 useEffect bind 读到空缓存。
 */
function bindIpeUserLocalFromUser(user) {
  const ownerId = resolveIpeOwnerId(user);
  if (!ownerId) return null;
  bindIpeUserLocalOwner(ownerId);
  syncImageSpaceCreatorContext(user, ownerId, user?.teamId ?? null);
  return ownerId;
}

/**
 * 冷启动：若 session 缓存可用，同步恢复登录态并 bind，
 * 使 IpeRequireAuth 首屏即可挂载业务页且本地缓存可读。
 */
function readInitialSessionAuthState() {
  if (typeof window === 'undefined') {
    return {
      user: null,
      sessionVerified: false,
      hasToken: false,
      bootstrapping: true,
    };
  }
  const hasToken = Boolean(getFundingToken());
  if (!hasToken) {
    return {
      user: null,
      sessionVerified: false,
      hasToken: false,
      bootstrapping: false,
    };
  }
  const cached = readIpeSessionCache();
  if (cached?.user) {
    bindIpeUserLocalFromUser(cached.user);
    markIpeAuthReady();
    return {
      user: cached.user,
      sessionVerified: true,
      hasToken: true,
      bootstrapping: false,
    };
  }
  return {
    user: null,
    sessionVerified: false,
    hasToken: true,
    bootstrapping: true,
  };
}

/**
 * 会话结束时统一清理本地态（session 缓存 + 用户作用域缓存/cleanup 注册表）。
 * logout / 401 / 午夜过期 / 账号注销 都必须走这里，避免漏清。
 */
function clearIpeAuthLocalCaches() {
  clearIpeSessionCache();
  clearIpeUserLocalState();
}

function afterAuthSupplementLocation() {
  supplementLoginLocationAfterAuth(
    (loginContext) => fundingAuthAPI.recordLoginEvent(loginContext),
  );
}

function isUserDeactivated(user) {
  if (!user) return false;
  const status = user.status;
  return status === 1 || status === '1';
}

function currentWorkbenchLocation() {
  if (typeof window === 'undefined') return { pathname: '', search: '', hash: '' };
  return {
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
  };
}

export function IpeAuthProvider({ children }) {
  const navigate = useNavigate();
  const initialAuth = useMemo(() => readInitialSessionAuthState(), []);
  const [user, setUser] = useState(initialAuth.user);
  const [bootstrapping, setBootstrapping] = useState(initialAuth.bootstrapping);
  const [loginSubmitting, setLoginSubmitting] = useState(false);
  const [registerSubmitting, setRegisterSubmitting] = useState(false);
  const [hasToken, setHasToken] = useState(initialAuth.hasToken);
  /** 仅 getMe 成功后为 true，避免 token 无效时仍显示已登录；有 session 缓存时可同步预热 */
  const [sessionVerified, setSessionVerified] = useState(initialAuth.sessionVerified);
  const [creditsDialogOpen, setCreditsDialogOpen] = useState(false);
  const [creditsDialogMessage, setCreditsDialogMessage] = useState('');
  const deactivatedHandledRef = useRef(false);
  const sessionVerifyTimerRef = useRef(null);
  const midnightLogoutTimerRef = useRef(null);

  const isAuthenticated =
    !bootstrapping && sessionVerified && Boolean(user) && Boolean(getFundingToken());

  /** 结束登录态：清本地缓存、清 token、重置 React 会话状态 */
  const endAuthSession = useCallback(() => {
    clearIpeAuthLocalCaches();
    fundingAuthAPI.logout();
    setHasToken(false);
    setUser(null);
    setSessionVerified(false);
    resetIpeAuthReady();
    setImageSpaceCreatorContext(null);
  }, []);

  const handleAccountDeactivated = useCallback((opts = {}) => {
    if (deactivatedHandledRef.current) return;
    deactivatedHandledRef.current = true;
    endAuthSession();
    showToast(opts?.reason === 'suspended' ? '该账号已停用' : '该账号已注销', 'warning', 4000);
    if (typeof window !== 'undefined' && !isIpeLoginPath(window.location.pathname)) {
      navigate(IPE_LOGIN_PATH, { replace: true });
    }
  }, [endAuthSession, navigate]);

  const refreshUser = useCallback(async () => {
    const tokenAtStart = getFundingToken();
    if (!tokenAtStart) {
      endAuthSession();
      return null;
    }
    try {
      const data = await fundingAuthAPI.getMe();
      const u = data?.user || data;
      if (!u) {
        if (getFundingToken() === tokenAtStart) {
          endAuthSession();
        }
        return null;
      }
      if (isUserDeactivated(u)) {
        handleAccountDeactivated();
        return null;
      }
      // 必须在 setState 之前 bind：下一帧子组件 useState(loadCache) 依赖已绑定的 owner
      bindIpeUserLocalFromUser(u);
      setUser(u);
      setHasToken(true);
      setSessionVerified(true);
      saveIpeSessionCache(u);
      markIpeAuthReady();
      return u;
    } catch (err) {
      if (isAccountDeactivatedError(err) || isAccountSuspendedError(err)) {
        return null;
      }
      const isAuthError =
        err?.status === 401 || err?.code === 'LOGIN_REQUIRED' || err?.code === 'OTHER_DEVICE_LOGGED_IN';
      if (isAuthError && getFundingToken() === tokenAtStart) {
        endAuthSession();
      }
      return null;
    }
  }, [endAuthSession, handleAccountDeactivated]);

  const redirectToLogin = useCallback(
    (location = currentWorkbenchLocation()) => {
      if (isIpeLoginPath(location.pathname)) return;
      navigate(buildLoginUrlWithRedirect(location), { replace: true });
    },
    [navigate],
  );

  const verifySessionOrLogout = useCallback(async () => {
    const tokenAtStart = getFundingToken();
    if (!tokenAtStart) return;
    try {
      await fundingAuthAPI.getMe();
    } catch (err) {
      const isAuthError =
        err?.status === 401 || err?.code === 'LOGIN_REQUIRED' || err?.code === 'OTHER_DEVICE_LOGGED_IN';
      if (isAuthError && getFundingToken() === tokenAtStart) {
        endAuthSession();
        redirectToLogin();
      }
    }
  }, [endAuthSession, redirectToLogin]);

  useEffect(() => {
    setIpeCreditsInsufficientHandler(({ message }) => {
      setCreditsDialogMessage(message || '');
      setCreditsDialogOpen(true);
    });
    return () => setIpeCreditsInsufficientHandler(null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    // 仅作 UI 解阻：有 token 但 getMe 挂起时结束 bootstrapping。
    // IpeRequireAuth 在仍有 token 且未 verified 时不会踢登录，只会继续展示验证中。
    const timeoutId = window.setTimeout(() => {
      if (!cancelled) setBootstrapping(false);
    }, 8000);

    const sleep = (ms) => new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });

    (async () => {
      try {
        const token = getFundingToken();
        if (!token) {
          clearIpeAuthLocalCaches();
          setHasToken(false);
          setUser(null);
          setSessionVerified(false);
          resetIpeAuthReady();
          setImageSpaceCreatorContext(null);
          return;
        }

        const cached = readIpeSessionCache();
        if (cached?.user && !cancelled) {
          bindIpeUserLocalFromUser(cached.user);
          setHasToken(true);
          setUser(cached.user);
          setSessionVerified(true);
          markIpeAuthReady();
          setBootstrapping(false);
          void refreshUser();
          return;
        }

        setHasToken(true);
        resetIpeAuthReady();
        // 新标签无缓存时：对瞬时网络失败重试，避免直接落到未登录页
        const maxAttempts = 3;
        for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
          if (cancelled) return;
          const u = await refreshUser();
          if (u || !getFundingToken()) break;
          if (attempt < maxAttempts - 1) {
            await sleep(400 * (attempt + 1));
          }
        }
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    })();

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [refreshUser]);

  useEffect(() => {
    if (sessionVerified && user) {
      // 兜底：确保 creator 上下文与 owner 与当前 user 一致（主路径已在 setState 前 bind）
      bindIpeUserLocalFromUser(user);
      return undefined;
    }
    setImageSpaceCreatorContext(null);
    return undefined;
  }, [sessionVerified, user]);

  useEffect(() => {
    if (!sessionVerified || !user) return undefined;
    const resyncCreatorContext = () => {
      syncImageSpaceCreatorContext(user, user?.id ?? user?._id, user?.teamId ?? null);
    };
    window.addEventListener('storage', resyncCreatorContext);
    window.addEventListener('ipe-workspace-mode-changed', resyncCreatorContext);
    return () => {
      window.removeEventListener('storage', resyncCreatorContext);
      window.removeEventListener('ipe-workspace-mode-changed', resyncCreatorContext);
    };
  }, [sessionVerified, user]);

  useEffect(() => {
    setIpeAccountDeactivatedHandler((opts) => {
      handleAccountDeactivated(opts);
    });
    return () => setIpeAccountDeactivatedHandler(null);
  }, [handleAccountDeactivated]);

  useEffect(() => {
    setIpeAuthRequiredHandler((opts = {}) => {
      if (opts?.immediate) {
        if (sessionVerifyTimerRef.current) {
          window.clearTimeout(sessionVerifyTimerRef.current);
          sessionVerifyTimerRef.current = null;
        }
        endAuthSession();
        redirectToLogin();
        return;
      }
      if (sessionVerifyTimerRef.current) {
        window.clearTimeout(sessionVerifyTimerRef.current);
      }
      sessionVerifyTimerRef.current = window.setTimeout(() => {
        sessionVerifyTimerRef.current = null;
        void verifySessionOrLogout();
      }, 600);
    });
    return () => {
      if (sessionVerifyTimerRef.current) {
        window.clearTimeout(sessionVerifyTimerRef.current);
        sessionVerifyTimerRef.current = null;
      }
      setIpeAuthRequiredHandler(null);
    };
  }, [endAuthSession, redirectToLogin, verifySessionOrLogout]);

  useEffect(() => {
    const onCreditsConsumed = () => {
      void refreshUser();
    };
    window.addEventListener('ipe:credits-consumed', onCreditsConsumed);
    return () => window.removeEventListener('ipe:credits-consumed', onCreditsConsumed);
  }, [refreshUser]);

  useEffect(() => {
    if (midnightLogoutTimerRef.current) {
      window.clearTimeout(midnightLogoutTimerRef.current);
      midnightLogoutTimerRef.current = null;
    }
    if (!hasToken || !sessionVerified) return undefined;

    const scheduleCalendarExpiryLogout = () => {
      const delay = msUntilJwtCalendarExpiry(getFundingToken());
      midnightLogoutTimerRef.current = window.setTimeout(() => {
        midnightLogoutTimerRef.current = null;
        if (!getFundingToken()) return;
        endAuthSession();
        showToast('登录已过期，请重新登录', 'info');
        redirectToLogin();
      }, Math.max(1000, delay));
    };

    scheduleCalendarExpiryLogout();
    return () => {
      if (midnightLogoutTimerRef.current) {
        window.clearTimeout(midnightLogoutTimerRef.current);
        midnightLogoutTimerRef.current = null;
      }
    };
  }, [hasToken, sessionVerified, endAuthSession, redirectToLogin]);

  const login = useCallback(
    async (phone, password, loginContext = {}) => {
      const p = String(phone || '').trim();
      if (!phonePattern.test(p)) {
        throw new Error('请输入正确的11位手机号');
      }
      if (!password) {
        throw new Error('请输入密码');
      }
      setLoginSubmitting(true);
      try {
        const data = await fundingAuthAPI.login(p, password, loginContext);
        deactivatedHandledRef.current = false;
        setSessionVerified(false);
        resetIpeAuthReady();
        if (!getFundingToken()) {
          throw new Error('登录成功但未收到令牌，请重试');
        }
        setHasToken(true);
        const refreshed = await refreshUser();
        if (!refreshed) {
          throw new Error('登录状态校验失败，请重新登录');
        }
        if (data?.accountDeletionCancelled === true) {
          showToast('当前注销账号已撤销，请重新提交注销申请。', 'warning', 3500);
        }
        if (loginContext?.agreedToTerms) {
          afterAuthSupplementLocation();
        }
        return data;
      } finally {
        setLoginSubmitting(false);
      }
    },
    [refreshUser],
  );

  const register = useCallback(
    async (phone, password, nickname, code, loginContext = {}) => {
      const p = String(phone || '').trim();
      if (!phonePattern.test(p)) {
        throw new Error('请输入正确的11位手机号');
      }
      if (!String(nickname || '').trim()) {
        throw new Error('请输入昵称');
      }
      const passwordError = validateIpePassword(password);
      if (passwordError) {
        throw new Error(passwordError);
      }
      if (!/^\d{6}$/.test(String(code || '').trim())) {
        throw new Error('请输入6位验证码');
      }
      setRegisterSubmitting(true);
      try {
        const data = await fundingAuthAPI.register(
          p,
          password,
          nickname.trim(),
          code.trim(),
          loginContext,
        );
        deactivatedHandledRef.current = false;
        setSessionVerified(false);
        resetIpeAuthReady();
        if (!getFundingToken()) {
          throw new Error('注册成功但未收到令牌，请重试');
        }
        setHasToken(true);
        const refreshed = await refreshUser();
        if (!refreshed) {
          throw new Error('登录状态校验失败，请重新登录');
        }
        if (data?.accountDeletionCancelled === true) {
          showToast('当前注销账号已撤销，请重新提交注销申请。', 'warning', 3500);
        }
        if (loginContext?.agreedToTerms) {
          afterAuthSupplementLocation();
        }
        return data;
      } finally {
        setRegisterSubmitting(false);
      }
    },
    [refreshUser],
  );

  const loginBySms = useCallback(
    async (phone, code, loginContext = {}, captchaToken) => {
      const p = String(phone || '').trim();
      if (!phonePattern.test(p)) {
        throw new Error('请输入正确的11位手机号');
      }
      if (!/^\d{6}$/.test(String(code || '').trim())) {
        throw new Error('请输入6位验证码');
      }
      setLoginSubmitting(true);
      try {
        const data = await fundingAuthAPI.loginBySms(p, code.trim(), loginContext, captchaToken);
        deactivatedHandledRef.current = false;
        setSessionVerified(false);
        resetIpeAuthReady();
        if (!getFundingToken()) {
          throw new Error('登录成功但未收到令牌，请重试');
        }
        setHasToken(true);
        const refreshed = await refreshUser();
        if (!refreshed) {
          throw new Error('登录状态校验失败，请重新登录');
        }
        if (data?.accountDeletionCancelled === true) {
          showToast('当前注销账号已撤销，请重新提交注销申请。', 'warning', 3500);
        }
        if (loginContext?.agreedToTerms) {
          afterAuthSupplementLocation();
        }
        return data;
      } finally {
        setLoginSubmitting(false);
      }
    },
    [refreshUser],
  );

  const resetPassword = useCallback(async (phone, code, newPassword) => {
    const p = String(phone || '').trim();
    if (!phonePattern.test(p)) {
      throw new Error('请输入正确的11位手机号');
    }
    if (!/^\d{6}$/.test(String(code || '').trim())) {
      throw new Error('请输入6位验证码');
    }
    const passwordError = validateIpePassword(newPassword);
    if (passwordError) {
      throw new Error(passwordError);
    }
    const result = await fundingAuthAPI.resetPassword(p, code.trim(), newPassword);
    // 重置会抬用户水位；若当前仍持有旧登录态，立刻清本地，避免旧票继续用
    if (getFundingToken()) {
      endAuthSession();
    }
    return result;
  }, [endAuthSession]);

  const sendVerificationCode = useCallback(async (phone, scene, captchaToken) => {
    const p = String(phone || '').trim();
    if (!phonePattern.test(p)) {
      throw new Error('请输入正确的11位手机号');
    }
    return fundingAuthAPI.sendVerificationCode(p, scene, captchaToken);
  }, []);

  const completeWeChatLogin = useCallback(
    async (token, loginContext = {}) => {
      if (!token) {
        throw new Error('微信登录失败');
      }
      setLoginSubmitting(true);
      try {
        setFundingToken(token);
        setToken(null);
        deactivatedHandledRef.current = false;
        setSessionVerified(false);
        resetIpeAuthReady();
        setHasToken(true);
        const refreshed = await refreshUser();
        if (!refreshed) {
          throw new Error('登录状态校验失败，请重新登录');
        }
        if (loginContext?.agreedToTerms) {
          afterAuthSupplementLocation();
        }
        return refreshed;
      } finally {
        setLoginSubmitting(false);
      }
    },
    [refreshUser],
  );

  const logout = useCallback(async () => {
    try {
      await fundingAuthAPI.logoutRemote();
    } catch {
      // 404 / 网络失败也必须清本地，保证退出可预期
    } finally {
      endAuthSession();
      navigate(IPE_LOGIN_PATH, { replace: true });
    }
  }, [endAuthSession, navigate]);

  const requireAuth = useCallback(
    (action) => {
      if (getFundingToken() && user) {
        if (typeof action === 'function') return action();
        return true;
      }
      redirectToLogin();
      return false;
    },
    [redirectToLogin, user],
  );

  const goToLoginPage = useCallback(
    (redirectPath) => {
      if (redirectPath && isSafeWorkbenchRedirect(redirectPath)) {
        navigate(`${IPE_LOGIN_PATH}?redirect=${encodeURIComponent(redirectPath)}`);
        return;
      }
      redirectToLogin();
    },
    [navigate, redirectToLogin],
  );

  /** @deprecated 使用 goToLoginPage；保留别名避免旧代码报错 */
  const openLoginModal = goToLoginPage;

  const value = useMemo(
    () => ({
      user,
      isAuthenticated,
      sessionVerified,
      bootstrapping,
      loginSubmitting,
      registerSubmitting,
      login,
      loginBySms,
      register,
      resetPassword,
      sendVerificationCode,
      completeWeChatLogin,
      logout,
      requireAuth,
      openLoginModal,
      goToLoginPage,
      refreshUser,
    }),
    [
      user,
      isAuthenticated,
      sessionVerified,
      bootstrapping,
      loginSubmitting,
      registerSubmitting,
      login,
      loginBySms,
      register,
      resetPassword,
      sendVerificationCode,
      completeWeChatLogin,
      logout,
      requireAuth,
      openLoginModal,
      goToLoginPage,
      refreshUser,
    ],
  );

  return (
    <IpeAuthContext.Provider value={value}>
      {children}
      <IpeCreditsInsufficientDialog
        open={creditsDialogOpen}
        message={creditsDialogMessage}
        onClose={() => {
          setCreditsDialogOpen(false);
          refreshUser();
        }}
        onOpenAccountCenter={() => {
          window.dispatchEvent(new CustomEvent('ipe:open-account-center'));
        }}
      />
    </IpeAuthContext.Provider>
  );
}

export function useIpeAuth() {
  const ctx = useContext(IpeAuthContext);
  if (!ctx) {
    throw new Error('useIpeAuth 必须在 IpeAuthProvider 内使用');
  }
  return ctx;
}
